import { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError, formatDate } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Combobox } from "@/components/ui/combobox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, Search, Upload, Paperclip, Download, FileText, X, Users as UsersIcon } from "lucide-react";
import { toast } from "sonner";
import ConfirmDialog from "@/components/ConfirmDialog";

const STATUSES = [
  { value: "member", label: "Member" },
  { value: "attendee", label: "Attendee" },
  { value: "visitor", label: "Visitor" },
  { value: "former", label: "Former" },
];

const blank = {
  first_name: "", middle_name: "", last_name: "",
  email: "", phone: "", address: "",
  membership_status: "member", active: true,
  join_date: "", birthday: "",
  ministry_ids: [], donor_id: "", notes: "",
};

export default function MembersPage() {
  const { activeOrgId } = useAuth();
  const [items, setItems] = useState([]);
  const [donors, setDonors] = useState([]);
  const [ministries, setMinistries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [activeOnly, setActiveOnly] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [submitting, setSubmitting] = useState(false);
  const [removeTarget, setRemoveTarget] = useState(null);
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const fetchAll = async () => {
    if (!activeOrgId) return;
    setLoading(true);
    try {
      const [mem, dnr, min] = await Promise.all([
        api.get(`/organizations/${activeOrgId}/directory`),
        api.get(`/organizations/${activeOrgId}/donors`).catch(() => ({ data: [] })),
        api.get(`/organizations/${activeOrgId}/ministries`).catch(() => ({ data: [] })),
      ]);
      setItems(mem.data);
      setDonors(dnr.data);
      setMinistries(min.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, [activeOrgId]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((m) => {
      if (activeOnly && !m.active) return false;
      if (!q) return true;
      return (
        m.name.toLowerCase().includes(q) ||
        (m.email || "").toLowerCase().includes(q) ||
        (m.phone || "").toLowerCase().includes(q) ||
        (m.ministry_names || []).some((n) => n.toLowerCase().includes(q))
      );
    });
  }, [items, search, activeOnly]);

  const openNew = () => { setEditing(null); setForm(blank); setDialogOpen(true); };
  const openEdit = (m) => {
    setEditing(m);
    setForm({
      first_name: m.first_name || "", middle_name: m.middle_name || "", last_name: m.last_name || "",
      email: m.email || "", phone: m.phone || "", address: m.address || "",
      membership_status: m.membership_status || "member",
      active: m.active !== false,
      join_date: m.join_date || "", birthday: m.birthday || "",
      ministry_ids: m.ministry_ids || [],
      donor_id: m.donor_id || "",
      notes: m.notes || "",
    });
    setDialogOpen(true);
  };

  const createMinistryInline = async (name) => {
    try {
      const { data } = await api.post(`/organizations/${activeOrgId}/ministries`, { name: name.trim() });
      setMinistries((arr) => arr.find((x) => x.id === data.id) ? arr : [...arr, { id: data.id, name: data.name }]);
      toast.success(`Ministry "${data.name}" added`);
      return { value: data.id, label: data.name };
    } catch (e) { toast.error(formatApiError(e)); return null; }
  };

  const toggleMinistry = (id) => setForm((f) => ({
    ...f,
    ministry_ids: f.ministry_ids.includes(id) ? f.ministry_ids.filter((x) => x !== id) : [...f.ministry_ids, id],
  }));

  const submit = async () => {
    if (!form.first_name.trim()) return toast.error("First name is required");
    setSubmitting(true);
    try {
      const payload = { ...form, donor_id: form.donor_id || null, join_date: form.join_date || null, birthday: form.birthday || null };
      if (editing) await api.patch(`/organizations/${activeOrgId}/directory/${editing.id}`, payload);
      else {
        const { data } = await api.post(`/organizations/${activeOrgId}/directory`, payload);
        setEditing(data); // allow uploading files right after create without closing
      }
      toast.success(editing ? "Member updated" : "Member added");
      fetchAll();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSubmitting(false); }
  };

  const doDelete = async () => {
    if (!removeTarget) return;
    try {
      await api.delete(`/organizations/${activeOrgId}/directory/${removeTarget.id}`);
      toast.success("Member removed"); setRemoveTarget(null); fetchAll();
    } catch (e) { toast.error(formatApiError(e)); setRemoveTarget(null); }
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // reset so same file can be re-picked
    if (!file || !editing) return;
    if (file.size > 6 * 1024 * 1024) return toast.error("File too large (max 6MB)");
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post(`/organizations/${activeOrgId}/directory/${editing.id}/files`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Uploaded "${file.name}"`);
      const refreshed = await api.get(`/organizations/${activeOrgId}/directory`);
      setItems(refreshed.data);
      const ed = refreshed.data.find((m) => m.id === editing.id);
      if (ed) setEditing(ed);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); }
  };

  const downloadFile = async (f) => {
    try {
      const res = await api.get(`/organizations/${activeOrgId}/directory/${editing.id}/files/${f.id}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = f.name; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const removeFile = async (f) => {
    if (!editing) return;
    try {
      await api.delete(`/organizations/${activeOrgId}/directory/${editing.id}/files/${f.id}`);
      toast.success("File removed");
      const refreshed = await api.get(`/organizations/${activeOrgId}/directory`);
      setItems(refreshed.data);
      const ed = refreshed.data.find((m) => m.id === editing.id);
      if (ed) setEditing(ed);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-8 fade-up">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground">Directory</div>
          <h1 className="font-serif text-5xl tracking-tight mt-2">Members & ministries</h1>
          <p className="text-muted-foreground mt-2 max-w-md">
            Your church family in one place — track attendance status, ministries, link giving records, and store care notes or documents.
          </p>
        </div>
        <Button onClick={openNew} className="rounded-xl h-11" data-testid="add-member-button">
          <Plus className="w-4 h-4 mr-2" /> Add member
        </Button>
      </header>

      <Card className="rounded-2xl border-border shadow-none p-3 flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-1 min-w-[200px]">
          <Search className="w-4 h-4 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name, email, phone, ministry…" className="rounded-xl border-0 shadow-none focus-visible:ring-0" data-testid="member-search" />
        </div>
        <div className="flex items-center gap-2">
          <Switch checked={activeOnly} onCheckedChange={setActiveOnly} data-testid="member-active-only" />
          <span className="text-sm">Active only</span>
        </div>
        <span className="text-sm text-muted-foreground">{filtered.length} of {items.length}</span>
      </Card>

      {loading ? (
        <div className="text-muted-foreground">Loading…</div>
      ) : filtered.length === 0 ? (
        <Card className="rounded-2xl border-border shadow-none p-12 text-center">
          <UsersIcon className="w-7 h-7 mx-auto text-muted-foreground" />
          <div className="font-serif text-2xl mt-3">{items.length === 0 ? "No members yet" : "No matches"}</div>
          <p className="text-sm text-muted-foreground mt-1">{items.length === 0 ? "Add your first member above." : "Try a different search."}</p>
        </Card>
      ) : (
        <Card className="rounded-2xl border-border shadow-none overflow-hidden">
          <div className="divide-y divide-border">
            {filtered.map((m) => (
              <div key={m.id} className="px-5 py-4 flex items-center gap-4 hover:bg-secondary/40 cursor-pointer" onClick={() => openEdit(m)} data-testid={`member-row-${m.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium truncate">{m.name}</span>
                    <Badge variant="outline" className="text-[10px]">{m.membership_status}</Badge>
                    {!m.active && <Badge variant="outline" className="bg-muted text-muted-foreground text-[10px]">Inactive</Badge>}
                    {m.donor_id && <Badge variant="outline" className="bg-primary/5 text-primary border-primary/20 text-[10px]">Donor linked</Badge>}
                    {(m.files || []).length > 0 && <Badge variant="outline" className="text-[10px]"><Paperclip className="w-2.5 h-2.5 mr-0.5" />{m.files.length}</Badge>}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1 truncate">
                    {m.email && <span>{m.email}</span>}
                    {m.phone && <span>{m.email ? " · " : ""}{m.phone}</span>}
                  </div>
                  {(m.ministry_names || []).length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {m.ministry_names.map((n) => <Badge key={n} variant="outline" className="bg-secondary/60 text-[10px]">{n}</Badge>)}
                    </div>
                  )}
                </div>
                <Button size="icon" variant="ghost" className="rounded-xl text-destructive hover:bg-destructive/10" onClick={(e) => { e.stopPropagation(); setRemoveTarget(m); }} data-testid={`member-delete-${m.id}`}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        </Card>
      )}

      <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) setEditing(null); }}>
        <DialogContent className="rounded-3xl max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="member-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-3xl">{editing ? "Edit member" : "Add member"}</DialogTitle>
            <DialogDescription>Demographics, ministry involvement, donor link, notes, and files.</DialogDescription>
          </DialogHeader>

          <div className="space-y-5 py-2">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="space-y-1.5"><Label>First name <span className="text-destructive">*</span></Label>
                <Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="rounded-xl" data-testid="member-first-name" /></div>
              <div className="space-y-1.5"><Label>Middle</Label>
                <Input value={form.middle_name} onChange={(e) => setForm({ ...form, middle_name: e.target.value })} className="rounded-xl" /></div>
              <div className="space-y-1.5"><Label>Last</Label>
                <Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="rounded-xl" data-testid="member-last-name" /></div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Email</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-xl" /></div>
              <div className="space-y-1.5"><Label>Phone</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-xl" /></div>
            </div>

            <div className="space-y-1.5"><Label>Address</Label>
              <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="rounded-xl" /></div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="space-y-1.5"><Label>Status</Label>
                <Select value={form.membership_status} onValueChange={(v) => setForm({ ...form, membership_status: v })}>
                  <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="space-y-1.5"><Label>Join date</Label>
                <Input type="date" value={form.join_date} onChange={(e) => setForm({ ...form, join_date: e.target.value })} className="rounded-xl" /></div>
              <div className="space-y-1.5"><Label>Birthday</Label>
                <Input type="date" value={form.birthday} onChange={(e) => setForm({ ...form, birthday: e.target.value })} className="rounded-xl" /></div>
              <div className="space-y-1.5"><Label>Active</Label>
                <div className="h-10 flex items-center gap-2">
                  <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="member-active-switch" />
                  <span className="text-xs text-muted-foreground">{form.active ? "Active" : "Inactive"}</span>
                </div></div>
            </div>

            <div className="space-y-1.5">
              <Label>Linked donor profile</Label>
              <Select value={form.donor_id || "_none"} onValueChange={(v) => setForm({ ...form, donor_id: v === "_none" ? "" : v })}>
                <SelectTrigger className="rounded-xl" data-testid="member-donor-select"><SelectValue placeholder="None — not linked to giving" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">— Not linked —</SelectItem>
                  {donors.filter((d) => d.active !== false).map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground">Linking lets giving history attach to this member record.</p>
            </div>

            <div className="space-y-1.5">
              <Label>Ministries involved in</Label>
              <div className="flex flex-wrap gap-1.5">
                {ministries.map((m) => {
                  const on = form.ministry_ids.includes(m.id);
                  return (
                    <button key={m.id} type="button" onClick={() => toggleMinistry(m.id)} className={`text-xs px-3 py-1.5 rounded-full border transition-all ${on ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border hover:border-primary/50"}`} data-testid={`ministry-chip-${m.id}`}>
                      {m.name}
                    </button>
                  );
                })}
              </div>
              <div className="mt-2">
                <Combobox
                  value=""
                  onValueChange={() => {}}
                  items={[]}
                  placeholder="Add or create a ministry…"
                  searchPlaceholder="Type ministry name…"
                  emptyMessage="Type a name to create"
                  onCreate={async (name) => {
                    const r = await createMinistryInline(name);
                    if (r) {
                      setForm((f) => f.ministry_ids.includes(r.value) ? f : { ...f, ministry_ids: [...f.ministry_ids, r.value] });
                    }
                    return r;
                  }}
                  testid="member-ministry-combobox"
                />
              </div>
            </div>

            <div className="space-y-1.5"><Label>Notes</Label>
              <Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-xl" placeholder="Care notes, prayer requests, conversation log…" data-testid="member-notes" /></div>

            {editing && (
              <div className="space-y-2 rounded-2xl border border-border p-4">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-semibold flex items-center gap-2"><Paperclip className="w-4 h-4 text-muted-foreground" /> Files · {(editing.files || []).length}</div>
                  <Button type="button" variant="outline" size="sm" className="rounded-xl" onClick={() => fileInputRef.current?.click()} disabled={uploading} data-testid="member-upload-button">
                    <Upload className="w-3.5 h-3.5 mr-1" /> {uploading ? "Uploading…" : "Upload"}
                  </Button>
                  <input ref={fileInputRef} type="file" hidden onChange={handleFile} />
                </div>
                {(editing.files || []).length === 0 ? (
                  <p className="text-xs text-muted-foreground">No files yet. Membership card, ID copies, signed forms — up to 6MB each.</p>
                ) : (
                  <div className="divide-y divide-border -mx-1">
                    {editing.files.map((f) => (
                      <div key={f.id} className="px-1 py-2 flex items-center gap-2" data-testid={`member-file-${f.id}`}>
                        <FileText className="w-4 h-4 text-muted-foreground" />
                        <div className="min-w-0 flex-1">
                          <div className="text-sm truncate">{f.name}</div>
                          <div className="text-[10px] text-muted-foreground">{(f.size / 1024).toFixed(1)} KB · {formatDate(f.uploaded_at)}</div>
                        </div>
                        <Button size="icon" variant="ghost" className="rounded-xl h-8 w-8" onClick={() => downloadFile(f)} data-testid={`member-file-download-${f.id}`}><Download className="w-3.5 h-3.5" /></Button>
                        <Button size="icon" variant="ghost" className="rounded-xl h-8 w-8 text-destructive hover:bg-destructive/10" onClick={() => removeFile(f)} data-testid={`member-file-delete-${f.id}`}><X className="w-3.5 h-3.5" /></Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="ghost" className="rounded-xl" onClick={() => setDialogOpen(false)}>Close</Button>
            <Button onClick={submit} disabled={submitting} className="rounded-xl soft-glow" data-testid="member-save-button">
              {submitting ? "Saving…" : editing ? "Save changes" : "Add member"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!removeTarget}
        onOpenChange={(o) => { if (!o) setRemoveTarget(null); }}
        title={`Remove ${removeTarget?.name}?`}
        description="This removes the member record and any attached files. Linked donor profile and giving history are unaffected."
        confirmLabel="Remove"
        onConfirm={doDelete}
        testid="confirm-delete-member"
      />
    </div>
  );
}
